/**
 * Where sign-in should send someone back to.
 *
 * SIMS-9057: a coordinator following a link into the backoffice while signed
 * out was sent to sign in and then dropped on the dashboard, losing the record
 * they were trying to reach.
 *
 * SIMS-9109: setting `appState.returnTo` was necessary but not sufficient. The
 * value arrives in `onRedirectCallback`, which lives OUTSIDE the Router and so
 * cannot navigate — it used to push onto a standalone `createBrowserHistory()`
 * instance that `<BrowserRouter>` has never listened to. `<Callback>` is inside
 * the Router and can navigate, but never sees `appState`. These two functions
 * are the hand-off between them: remembered where the value is delivered,
 * consumed where it can be acted on.
 */

/**
 * Paths that must never be returned to.
 *
 * `/callback` is Auth0's own landing page: returning there would hand the SDK
 * a second authorization response it has no code for, and the user would end
 * up back at sign-in. `/unauthorized` is where a rejected route sends people,
 * so returning to it would strand them on the rejection rather than on what
 * they asked for.
 */
const NEVER_RETURN_TO = ["/callback", "/unauthorized"];

const STORAGE_KEY = "sims-return-to";

/**
 * Whether a candidate destination is one we are willing to navigate to.
 *
 * The path must be same-origin and relative. `//host` and `/\host` are both
 * read as protocol-relative URLs by browsers, so a value that survived a round
 * trip through Auth0's transaction store and sessionStorage could otherwise
 * redirect off-site after a successful sign-in. `resolveReturnTo` only ever
 * builds these from `window.location`, but the value is re-validated at the
 * point of use rather than trusted because it was validated once.
 */
const isSafeReturnTo = (target: string | undefined): target is string => {
	if (!target || !target.startsWith("/")) return false;
	if (target.startsWith("//") || target.startsWith("/\\")) return false;

	return !NEVER_RETURN_TO.some((path) => target.startsWith(path));
};

/**
 * The location to resume after sign-in, or undefined when there is nothing
 * worth resuming.
 *
 * Undefined rather than "/" on purpose: the consumer distinguishes the two, and
 * a caller that always supplies a destination would override whatever default
 * it decides on.
 *
 * The query string is kept — a link to a filtered report is a different
 * destination from the report — and the hash with it.
 */
export const resolveReturnTo = (
	location: Pick<Location, "pathname" | "search" | "hash">,
): string | undefined => {
	const { pathname, search, hash } = location;

	if (!pathname || pathname === "/") return undefined;
	if (NEVER_RETURN_TO.some((path) => pathname.startsWith(path))) {
		return undefined;
	}

	return `${pathname}${search ?? ""}${hash ?? ""}`;
};

/**
 * Hold a destination until the Router is mounted and able to navigate.
 *
 * sessionStorage rather than a module variable so the hand-off survives a
 * reload between the callback and the render, and tab-scoped so a second tab
 * signing in normally is not dragged somewhere the first tab asked for. The
 * path is already in the address bar and the browser's own history, so storing
 * it adds no exposure.
 *
 * An absent or unusable destination CLEARS any previous one. A stale value from
 * an abandoned sign-in must not redirect the next one.
 */
export const rememberReturnTo = (target: string | undefined): void => {
	try {
		if (isSafeReturnTo(target)) {
			window.sessionStorage.setItem(STORAGE_KEY, target);
		} else {
			window.sessionStorage.removeItem(STORAGE_KEY);
		}
	} catch {
		// Storage can be unavailable (private browsing, blocked cookies). Losing
		// the destination costs the user a dashboard landing, not a failed
		// sign-in, so this must never throw into the auth flow.
	}
};

/**
 * Take the remembered destination, if there is a usable one, and forget it.
 *
 * Reading is destructive: a destination is good for exactly one sign-in, and
 * leaving it behind would send the user there again on their next one.
 */
export const consumeReturnTo = (): string | undefined => {
	try {
		const stored = window.sessionStorage.getItem(STORAGE_KEY) ?? undefined;
		window.sessionStorage.removeItem(STORAGE_KEY);

		return isSafeReturnTo(stored) ? stored : undefined;
	} catch {
		return undefined;
	}
};
